import React, { useEffect, useRef, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import Navbar from "@/components/Navbar";
import { Crown, Check } from "lucide-react";

export default function BillingSuccess() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const { refresh } = useAuth();
  const [status, setStatus] = useState("checking");
  const [tier, setTier] = useState("");
  const polled = useRef(0);

  useEffect(() => {
    const sid = params.get("session_id");
    if (!sid) { navigate("/pricing"); return; }
    let cancelled = false;

    const poll = async () => {
      if (cancelled) return;
      polled.current += 1;
      try {
        const { data } = await api.get(`/billing/status/${sid}`);
        if (data.payment_status === "paid") {
          setStatus("paid");
          setTier(data.tier);
          await refresh();
          return;
        }
        if (data.status === "expired") {
          setStatus("expired");
          return;
        }
        if (polled.current < 8) setTimeout(poll, 2000);
        else setStatus("timeout");
      } catch {
        setStatus("error");
      }
    };
    poll();
    return () => { cancelled = true; };
  }, [params, navigate, refresh]);

  return (
    <div className="min-h-screen bg-[#1A1A1A] text-[#F5F5F5]">
      <Navbar />
      <div className="max-w-2xl mx-auto px-6 py-24 text-center">
        <div className="goji-glass-strong p-12">
          {status === "checking" && (
            <>
              <p className="font-display text-3xl italic text-[#D4AF37]" data-testid="billing-checking">Confirming your membership...</p>
            </>
          )}
          {status === "paid" && (
            <div data-testid="billing-success">
              <div className="w-16 h-16 mx-auto border border-[#D4AF37] flex items-center justify-center">
                <Check className="w-7 h-7 text-[#D4AF37]" />
              </div>
              <h1 className="mt-8 font-display text-5xl font-normal text-[#F5F5F5]">You are a <span className="italic text-[#D4AF37]">{tier}.</span></h1>
              <p className="mt-5 text-[#F5F5F5]/70 flex items-center justify-center gap-2">Membership granted. <Crown className="w-4 h-4 text-[#D4AF37]" /></p>
              <button onClick={() => navigate("/dashboard")} className="mt-10 btn-ghost" data-testid="billing-continue-button">
                Enter Goji
              </button>
            </div>
          )}
          {status === "expired" && (
            <div data-testid="billing-expired">
              <p className="font-display text-3xl italic">Session expired.</p>
              <button onClick={() => navigate("/pricing")} className="mt-8 btn-ghost">Try again</button>
            </div>
          )}
          {(status === "timeout" || status === "error") && (
            <div data-testid="billing-error">
              <p className="font-display text-2xl italic">We are confirming your payment.</p>
              <p className="text-sm text-[#F5F5F5]/55 mt-3">Please check your email or refresh shortly.</p>
              <button onClick={() => navigate("/dashboard")} className="mt-8 btn-ghost">Dashboard</button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
